/**
 * TelemetryBootstrap — Handles telemetry lifecycle and page-view tracking.
 *
 * - Restores telemetry SDK when a returning user reloads the page
 * - Stops telemetry when the user logs out
 * - Tracks page views via React Router location changes
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getTelemetry,
  startTelemetry,
  stopTelemetry,
} from "../lib/telemetry-client";
import {
  backgroundDbWorkRefetchIntervalMs,
  isBackgroundDbWorkPaused,
} from "../lib/background-db-work-pause";
import { useAuthStore } from "../stores/authStore";

export function TelemetryBootstrap(): null {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const prevPath = useRef("");
  const initializedForUser = useRef<string | null>(null);

  // Initialize or tear down SDK based on auth state
  useEffect(() => {
    let resumeTimer: ReturnType<typeof setTimeout> | undefined;

    const synchronizeTelemetry = () => {
      if (isAuthenticated && user && isBackgroundDbWorkPaused()) {
        stopTelemetry();
        initializedForUser.current = null;
        resumeTimer = setTimeout(
          synchronizeTelemetry,
          backgroundDbWorkRefetchIntervalMs(60_000),
        );
        return;
      }

      if (isAuthenticated && user && user.id !== initializedForUser.current) {
        // Start telemetry for this user (handles previous instance cleanup)
        startTelemetry({
          id: user.id,
          tenantId: user.tenantId,
          storeId: user.storeId,
        });
        initializedForUser.current = user.id;
      }

      if (!isAuthenticated && initializedForUser.current) {
        stopTelemetry();
        initializedForUser.current = null;
      }
    };

    synchronizeTelemetry();
    return () => {
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  }, [isAuthenticated, user]);

  // Track page views on route changes
  useEffect(() => {
    const sdk = getTelemetry();
    if (!sdk) return;

    const path = location.pathname;
    if (path !== prevPath.current) {
      sdk.trackPageView(path, {
        search: location.search || undefined,
        hash: location.hash || undefined,
      });
      prevPath.current = path;
    }
  }, [location]);

  return null;
}
